/**
 * Utilities for displaying verification freshness state.
 * Used on compare table rows, firm detail pages, and featured cards.
 */

export type VerificationState = 'verified' | 'stale' | 'unverified';

const FRESHNESS_THRESHOLD_DAYS = 14;

export function getVerificationState(lastVerifiedAt: string | null): VerificationState {
  if (!lastVerifiedAt) return 'unverified';
  const verifiedDate = new Date(lastVerifiedAt);
  const daysSince = (Date.now() - verifiedDate.getTime()) / (1000 * 60 * 60 * 24);
  if (daysSince <= FRESHNESS_THRESHOLD_DAYS) return 'verified';
  return 'stale';
}

export function formatVerifiedDate(lastVerifiedAt: string | null): string {
  if (!lastVerifiedAt) return 'Verification date unavailable';
  const d = new Date(lastVerifiedAt);
  return `Verified ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export function getVerificationLabel(state: VerificationState): string {
  switch (state) {
    case 'verified': return 'Recently verified';
    case 'stale': return 'Needs recheck';
    case 'unverified': return 'Not yet verified';
  }
}

export function getVerificationColor(state: VerificationState) {
  switch (state) {
    case 'verified':
      return {
        text: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        dot: 'bg-emerald-500',
      };
    case 'stale':
      return {
        text: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        dot: 'bg-amber-500',
      };
    case 'unverified':
      return {
        text: 'text-gray-500 dark:text-gray-400',
        bg: 'bg-gray-50 dark:bg-gray-800',
        border: 'border-gray-200 dark:border-gray-700',
        dot: 'bg-gray-400',
      };
  }
}

/** Returns true if a discount is genuinely verified (has a real lower price). */
export function hasVerifiedDiscount(
  basePrice: number | null,
  discountedPrice: number | null,
  lastVerifiedAt: string | null,
): boolean {
  if (basePrice == null || discountedPrice == null) return false;
  if (discountedPrice >= basePrice) return false;
  return getVerificationState(lastVerifiedAt) !== 'unverified';
}

/** CTA label based on affiliate and promo state. */
export function getCtaLabel(
  hasAffiliate: boolean,
  hasVerifiedPromo: boolean,
): string {
  if (!hasAffiliate) return 'Visit Site';
  if (hasVerifiedPromo) return 'Get Deal';
  return 'Get Started';
}
